import React, { useState } from 'react';
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetError,
  UseFormSetValue,
  UseFormClearErrors,
} from 'react-hook-form';
import { FiTrash2, FiUpload } from 'react-icons/fi';
import { Cropper, ImageRestriction, Priority } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { BsInfoCircle } from 'react-icons/bs';

type Dimentions = {
  width: number;
  height: number;
};
interface FileUploadProps {
  name: string;
  label: string;
  setPreview?: (preview: string) => void;
  register: UseFormRegister<any>;
  errors: FieldErrors;
  description?: string;
  watch: any;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  setValue: UseFormSetValue<any>;
  accept?: string;
  requiredMessage?: string;
  isRequired?: boolean;
  isDraft?: boolean;
  isEditing?: boolean;
  dimentions?: Dimentions;
  excludedFromSizeLimit?: boolean;
  maxFileSizeMB?: number;
  onFileReady?: (file: File) => void;
  placeholder?: string;
  backendFileName?: string;
  backendFile?: string;
  isLoading: boolean;
  cropAspect?: number;
  readOnly?: boolean;
  moreDetails?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  label,
  register,
  errors,
  watch,
  accept = '*/*',
  requiredMessage,
  dimentions = { width: 800, height: 400 },
  isRequired = false,
  isDraft = false,
  excludedFromSizeLimit = false,
  maxFileSizeMB = 2,
  onFileReady,
  readOnly = false,
  isEditing = false,
  backendFileName = '',
  description,
  setError,
  clearErrors,
  setValue,
  setPreview,
  isLoading = true,
  cropAspect = 1,
  backendFile,
  moreDetails,
}) => {
  const fileList: FileList = watch(name);
  const fileFromUser = fileList?.[0];
  const fileNameToShow = fileFromUser?.name || backendFileName || '';

  // crop states
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropper, setCropper] = useState<any>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  // const [imageAspect, setImageAspect] = useState<number>(cropAspect);
  const [cropperDimentions, setCropperDimentions] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });

  const handleSaveCroppedImage = async () => {
    if (!cropper) return;

    // نجيب الصورة المقصوصة كـ canvas
    const canvas = cropper.getCanvas({
      width: dimentions?.width,
      height: dimentions?.height,
    });

    if (!canvas) return;

    return new Promise<void>(resolve => {
      canvas.toBlob((blob: any) => {
        if (!blob) return;

        const croppedFile = new File([blob], fileNameToShow, {
          type: 'image/png',
        });

        // نحول الملف إلى FileList عشان RHF
        const dt = new DataTransfer();
        dt.items.add(croppedFile);
        setValue(name, dt.files);
        clearErrors(name);
        onFileReady?.(croppedFile);
        setCropSrc(null);
        resolve();
      }, 'image/png');
    });
  };

  const formatFieldName = (fieldName: string) => {
    if (fieldName.toLowerCase().startsWith('logo')) {
      return (
        'Logo ' +
        fieldName.slice(4).charAt(0).toUpperCase() +
        fieldName.slice(5)
      );
    }
    return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!excludedFromSizeLimit && file.size > maxFileSizeMB * 1024 * 1024) {
      setError(name, {
        type: 'manual',
        message: `Max File Size Allowed is ${maxFileSizeMB}MB`,
      });
      setValue(name, null);
      e.target.value = '';
      return;
    }

    if (file.type.startsWith('image/')) {
      // Create a temporary image to check dimensions before processing
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        // Clean up the object URL
        URL.revokeObjectURL(objectUrl);

        // Check if image dimensions are smaller than required dimensions
        if (img.width < dimentions.width && img.height < dimentions.height) {
          setError(name, {
            type: 'manual',
            message: `${formatFieldName(name)} provided does not meet the minimum requirements of ${dimentions.width}x${dimentions.height} pixels. Please ensure you upload a ${formatFieldName(name)} to meet the minimum requirements`,
          });
          setValue(name, null);
          e.target.value = '';
          return;
        }

        // If dimensions are valid, proceed with file reading and cropping
        const reader = new FileReader();
        reader.onload = () => {
          setCropperDimentions({
            width: img.width,
            height: img.height,
          });
          setCropSrc(reader.result as string);
          // setImageAspect(img.width / img.height);
        };
        reader.readAsDataURL(file);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setError(name, {
          type: 'manual',
          message: 'Invalid image file',
        });
        setValue(name, null);
        e.target.value = '';
      };

      img.src = objectUrl;
    } else {
      clearErrors(name);
      onFileReady?.(file);
    }
  };

  if (isLoading) {
    return (
      <div>
        <label className='block text-sm font-medium text-gray-700 mb-4'>
          &nbsp;
        </label>
        <div className='flex items-center w-full space-x-2 relative animate-pulse h-12'>
          <div className='w-full h-10 bg-gray-200 rounded-md' />
          <div className='w-1/5 h-10 flex rounded border border-gray-300 shadow-inner overflow-hidden'>
            <div className='bg-[#aeaeae]' />
          </div>
        </div>
      </div>
    );
  }

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTooltipVisible(prev => !prev);
  };

  const handleMouseEnter = () => setIsTooltipVisible(true);
  const handleMouseLeave = () => setIsTooltipVisible(false);

  return (
    <div>
      <div className='flex justify-between items-center mb-2'>
        <label className='block text-[#000000] text-sm font-[500]'>
          {label} {isRequired && <span className='text-red-600'>*</span>}
        </label>
        {moreDetails && (
          <div
            className='relative group inline-block'
            onClick={handleInfoClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <BsInfoCircle
              color='#1133BB'
              size={18}
              className='pointer-events-auto cursor-pointer'
            />
            {isTooltipVisible && (
              <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                <p className='text-sm  whitespace-normal'>{moreDetails}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className='flex items-center w-full'>
        <input
          type='text'
          value={fileNameToShow}
          readOnly={readOnly}
          placeholder='No file selected (Max file size 2Mb)'
          className={`register-input w-4/5 rounded-r-[0px] ${
            !isEditing && 'read-only-input'
          }`}
        />

        <input
          type='file'
          id={name}
          accept={accept}
          className='hidden'
          readOnly={readOnly && !isEditing}
          {...register(name, {
            required:
              isRequired && !isDraft && !fileNameToShow
                ? requiredMessage
                : false,
            onChange: e => handleFileChange(e),
          })}
        />

        <div className='w-1/5 flex rounded border border-gray-300 shadow-inner overflow-hidden'>
          <label
            htmlFor={name}
            className='flex-1 text-[0.75rem] cursor-pointer py-2 md:py-4 px-1 bg-[#aeaeae] text-white text-center hover:bg-[#9a9a9a] flex justify-center items-center rounded-r-none'
          >
            <FiUpload size={18} />
          </label>

          <button
            type='button'
            onClick={() => {
              setValue(name, null);
              clearErrors(name);
              setPreview?.(backendFile || '');
              setCropSrc(null);
              // setImageAspect(cropAspect);
            }}
            className='flex-1 text-[0.75rem] py-2 md:py-4 px-1 flex justify-center items-center rounded-r-[4px]'
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      </div>

      {description && !errors[name]?.message && (
        <p className='text-[0.75rem] mt-1 text-[#878787]'>{description}</p>
      )}

      {errors[name]?.message && (
        <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
          {errors[name]?.message as string}
        </p>
      )}

      {/* مودال الكروب */}
      {cropSrc && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className={`bg-white p-4 rounded-md ${
              dimentions?.width && `w-[${dimentions.width + 100}px]`
            }`}
          >
            <div
              className={`relative w-full bg-gray-200 flex justify-center items-center ${
                dimentions?.height && `h-[${dimentions.height}px]`
              }`}
            >
              <Cropper
                priority={Priority.visibleArea}
                defaultSize={{
                  width: cropperDimentions?.width - 10,
                  height: cropperDimentions?.height - 10,
                }}
                style={{
                  backgroundColor: '#F8F9FF',
                  width: dimentions?.width,
                  height: dimentions?.height,
                }}
                src={cropSrc}
                stencilProps={{
                  handlers: true,
                  aspectRatio:
                    dimentions.width === 400 && dimentions.height === 190
                      ? 400 / 190
                      : undefined,
                  movable: true,
                  resizable: true,
                }}
                imageRestriction={ImageRestriction.none}
                onReady={setCropper}
                className='cropper bg-red-500'
              />
            </div>

            <div className='flex justify-end gap-2 mt-4'>
              <button
                onClick={() => {
                  setCropSrc(null);
                  setValue(name, null);
                  clearErrors(name);
                  setPreview?.(backendFile || '');
                  // setImageAspect(cropAspect);
                }}
                className='px-4 py-2 bg-gray-300 rounded-md'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleSaveCroppedImage}
                className='px-4 py-2 bg-blue-500 text-white rounded-md'
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
